<script lang="ts">
import { defineComponent, ref, onMounted, computed } from 'vue'
import { createNamespacedHelpers } from 'vuex-composition-helpers'
import * as querystring from 'querystring'
import { v4 as uuid } from 'uuid'
import { LOCAL_STORAGE_PASSKEY_STEP_UP_PASSKEY_ID_KEY, LOCAL_STORAGE_REDIRECT_URI_PASSKEY_KEY } from '@/constants'
import { getAuthXToken } from '@/services/auth'
import { isAllowedRedirectUri } from '@/services/trusted-device/is-allowed-redirect-uri'
import { router, useRoute } from '../../router'
import { useI18n } from '../../setup/setup-i18n'
import { redirectToUniversalLogin } from '../../services/auth/universal'
import { PasskeyPurpose } from '../../types'

const { useState: useUserStoreState, useActions: useUserStoreActions } = createNamespacedHelpers('User')
const { useState: useLoginDomainStoreState, useGetters: useLoginDomainStoreGetters } = createNamespacedHelpers('LoginDomain')

export default defineComponent({

  name: 'PasskeyStepUpPage',

  setup() {

    const { locale } = useI18n()

    const { userIdentity } = useUserStoreState(['userIdentity'])

    const { tenant } = useLoginDomainStoreState(['tenant'])

    const { loginHelpUrl, loginHelpUrlLocales, logo } = useLoginDomainStoreGetters(['loginHelpUrl', 'loginHelpUrlLocales', 'logo'])
    const { createPasskeyState } = useUserStoreActions(['createPasskeyState'])

    const token = getAuthXToken()

    const currentRoute = useRoute()

    // this is the redirect uri from the product
    const getRedirectUri = (): string => {
      return currentRoute.value?.query.redirectUri as string
    }

    // the passkey this step-up authorises an action on (e.g. delete), from the product
    const getPasskeyId = (): string => {
      return currentRoute.value?.query.passkeyId as string
    }

    // the step-up state is appended to the redirect uri and can authorise a passkey delete, so it may
    // only be sent to an allowed product (open redirect protection)
    const isRedirectUriRejected = (): boolean => {
      const redirectUri = getRedirectUri()
      return !!redirectUri && !isAllowedRedirectUri(redirectUri)
    }

    const storeRedirectURI = () => {

      const redirectUri = getRedirectUri()

      if (isRedirectUriRejected()) {
        localStorage.removeItem(LOCAL_STORAGE_REDIRECT_URI_PASSKEY_KEY)
        localStorage.removeItem(LOCAL_STORAGE_PASSKEY_STEP_UP_PASSKEY_ID_KEY)
        return
      }

      if (redirectUri) {
        localStorage.setItem(LOCAL_STORAGE_REDIRECT_URI_PASSKEY_KEY, redirectUri)

        // the product's entry link carries the redirect uri; scope the passkey id to that flow so a
        // later step-up that names no passkey cannot pick up a stale one
        const passkeyId = getPasskeyId()

        if (passkeyId) {
          localStorage.setItem(LOCAL_STORAGE_PASSKEY_STEP_UP_PASSKEY_ID_KEY, passkeyId)
        } else {
          localStorage.removeItem(LOCAL_STORAGE_PASSKEY_STEP_UP_PASSKEY_ID_KEY)
        }
      }

    }

    // read once: the step-up is finished (or cancelled) so the passkey id is not needed again
    const consumePasskeyId = (): string | null => {
      const passkeyId = localStorage.getItem(LOCAL_STORAGE_PASSKEY_STEP_UP_PASSKEY_ID_KEY)
      localStorage.removeItem(LOCAL_STORAGE_PASSKEY_STEP_UP_PASSKEY_ID_KEY)
      return passkeyId
    }

    const getPurpose = (): string => {
      return currentRoute.value?.query.purpose as string
    }

    const getState = (): string => {
      return currentRoute.value?.query.state as string
    }

    const getLoginHelpUrl = computed(() => {

      if (!loginHelpUrlLocales.value || !locale) {
        return loginHelpUrl
      }

      return loginHelpUrlLocales.value[locale] || loginHelpUrlLocales.value.en

    })

    const loading = ref(true)

    storeRedirectURI()

    const getRedirectUrl = () => {

      const redirectUri = localStorage.getItem(LOCAL_STORAGE_REDIRECT_URI_PASSKEY_KEY)
      localStorage.removeItem(LOCAL_STORAGE_REDIRECT_URI_PASSKEY_KEY)

      // re-check before sending the state anywhere, in case a value was planted some other way
      if (!redirectUri || !isAllowedRedirectUri(redirectUri)) {
        throw new Error('RedirectUriNotAllowed')
      }

      return new URL(redirectUri)
    }

    onMounted(async () => {

      if (isRedirectUriRejected()) {
        router.replace({ name: 'UnknownErrorPage' })
        return
      }

      const purpose = getPurpose()

      if (!userIdentity.value) {
        router.replace({
          name: 'Root',
          query: {
            redirectUri: `${window.location.protocol}//${window.location.host}/authenticate?${querystring.stringify({
              purpose: PasskeyPurpose.passkeyStepUpAuthentication,
            })}`,
            purpose: PasskeyPurpose.passkeyStepUpAuthentication,
          },
        })
        return
      }

      try {

        // user will be redirected to auth0 to complete passkey step-up authentication
        // after which they will be redirected back to /authenticate with purpose=passkey-step-up-success to do token exchange with authx
        if (purpose === PasskeyPurpose.passkeyStepUpAuthentication) {

          const state = uuid()

          await redirectToUniversalLogin({
            tenant: tenant.value,
            redirectUri: `${window.location.protocol}//${window.location.host}/authenticate`,
            prompt: 'login',
            temporaryMfaSetup: false,
            purpose: PasskeyPurpose.passkeyStepUpAuthentication,
            email: userIdentity.value?.email,
            loginHelpUrl: getLoginHelpUrl.value,
            logoUri: logo.value,
            state,
            ui_locales: locale,
          })

        // If the user has cancelled the step-up authentication, skip state creation and redirect back to product
        } else if (purpose === PasskeyPurpose.passkeyStepUpCancelled) {

          consumePasskeyId()
          const redirectUrl = getRedirectUrl()
          window.location.assign(redirectUrl.toString())
        
        // If the user has finished passkey step-up authentication, we create passkey state in redis and redirect back to the product
        } else {

          // check passkey last auth at + create passkey state (bound to the passkey, if named)
          const passkeyId = consumePasskeyId()
          const response = await createPasskeyState({
            token: token.value,
            stateId: getState(),
            ...(passkeyId ? { passkeyId } : {}),
          })
          const redirectUrl = getRedirectUrl()
          redirectUrl.searchParams.append('state', response.passkeyState)

          window.location.assign(redirectUrl.toString())

        }

      } catch {
        router.replace({ name: 'UnknownErrorPage' })
      }

    })

    return {
      loading,
    }

  },

})

</script>

<template>
  <div>
    <div
      v-if="loading"
      class="loading"
    >
      <sm-loader />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.loading {
  @apply flex justify-center items-center min-h-screen;
}
</style>
